/*
 * Desktop header menus open on hover.
 *
 * Dawn opens its `<details>` menus on click only. This adds hover opening on
 * pointer devices at desktop width and leaves click, keyboard and touch behaviour
 * exactly as Dawn built them. The mega panel sits inside the same element, so
 * moving the pointer down into it keeps the menu open.
 *
 * Listeners are on the document rather than on each nav, so a section re-render in
 * the theme editor cannot leave the new markup unbound.
 */
(() => {
  const MEDIA = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const CLOSE_DELAY = 150;
  // Matches the mega-menu-out keyframes in component-mega-menu.css.
  const CLOSE_ANIMATION_MS = 200;
  let closeTimer = null;

  const animates = (details) =>
    details.classList.contains('mega-menu') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Opening needs no help: the panel animates itself in as soon as it renders.
  const open = (details) => {
    details.classList.remove('is-leaving');
    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  };

  const close = (details) => {
    if (!details) return;
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');

    if (!animates(details)) {
      details.removeAttribute('open');
      return;
    }

    details.classList.add('is-leaving');
    setTimeout(() => {
      // A pointer may have come back in the meantime, which cancels the close.
      if (details.classList.contains('is-leaving')) {
        details.removeAttribute('open');
        details.classList.remove('is-leaving');
      }
    }, CLOSE_ANIMATION_MS);
  };

  const closeAll = (keep) => {
    document.querySelectorAll('.header__inline-menu header-menu > details[open]').forEach((details) => {
      if (details !== keep) close(details);
    });
  };

  const menuFrom = (event) => {
    if (!MEDIA.matches || event.pointerType !== 'mouse') return null;
    const menu = event.target.closest('header-menu');
    return menu && menu.closest('.header__inline-menu') ? menu : null;
  };

  document.addEventListener('pointerover', (event) => {
    const menu = menuFrom(event);
    if (!menu) return;

    clearTimeout(closeTimer);

    const details = menu.querySelector('details');
    if (!details) return;

    // Coming back mid close-animation: cancel it rather than re-opening.
    if (details.hasAttribute('open')) {
      details.classList.remove('is-leaving');
      return;
    }

    closeAll(details);
    open(details);
  });

  document.addEventListener('pointerout', (event) => {
    const menu = menuFrom(event);
    if (!menu) return;

    // Ignore moves between elements inside the same menu.
    if (event.relatedTarget && menu.contains(event.relatedTarget)) return;

    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => close(menu.querySelector('details')), CLOSE_DELAY);
  });

  document.addEventListener('keyup', (event) => {
    if (event.code === 'Escape') closeAll(null);
  });
})();
